/**
 * PagBank JS SDK Integration for Card Tokenization
 * 
 * This service wraps the PagBank JS SDK (pagseguro.js) to:
 * 1. Initialize the SDK with public key
 * 2. Create card tokens via secure iframe/fields
 * 3. Return token (CARD_XXXX) to send to backend
 * 
 * Usage:
 *   import { initPagBankSDK, createCardToken } from './services/pagbankSDK';
 *   
 *   // On app init (once):
 *   await initPagBankSDK();
 *   
 *   // When user wants to save a card:
 *   const token = await createCardToken({
 *     number: '4111111111111111',
 *     cvv: '123',
 *     expiryMonth: '12',
 *     expiryYear: '2027',
 *     holderName: 'João Silva',
 *     holderCPF: '12345678900'
 *   });
 *   // token = 'CARD_ABC123...'
 */

let isInitialized = false;
let pagBankInstance = null;

/**
 * Initialize PagBank JS SDK
 * Must be called once on app startup (e.g., in App.js or main index.js)
 * 
 * @param {Object} options
 * @param {string} options.publicKey - PagBank public key (from PagBank dashboard)
 * @param {string} options.environment - 'sandbox' or 'production'
 * @returns {Promise<PagSeguroDirectPayment>} PagBank instance
 */
export async function initPagBankSDK({ publicKey, environment = 'sandbox' } = {}) {
  // Get config from window if not passed
  const config = window.PagBankConfig || {};
  const key = publicKey || config.publicKey;
  const env = environment || config.environment || 'sandbox';

  if (!key) {
    console.warn('[PagBank SDK] Public key not configured. Set window.PagBankConfig.publicKey or pass to initPagBankSDK()');
    return null;
  }

  if (isInitialized && pagBankInstance) {
    return pagBankInstance;
  }

  // Wait for PagSeguroDirectPayment to be available (script loaded)
  const waitForSDK = () => {
    return new Promise((resolve) => {
      if (window.PagSeguroDirectPayment) {
        resolve(window.PagSeguroDirectPayment);
      } else {
        // Script loads async, wait for it
        const checkInterval = setInterval(() => {
          if (window.PagSeguroDirectPayment) {
            clearInterval(checkInterval);
            resolve(window.PagSeguroDirectPayment);
          }
        }, 100);
        
        // Timeout after 10s
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve(null);
        }, 10000);
      }
    });
  };

  const PagSeguroDirectPayment = await waitForSDK();
  
  if (!PagSeguroDirectPayment) {
    console.error('[PagBank SDK] PagSeguroDirectPayment not loaded. Check script tag in index.html');
    return null;
  }

  // Set session ID (required for tokenization)
  try {
    await new Promise((resolve, reject) => {
      PagSeguroDirectPayment.setSessionId(
        generateSessionId(),
        {
          success: resolve,
          error: reject,
        }
      );
    });
  } catch (e) {
    console.warn('[PagBank SDK] setSessionId failed (may be ok for sandbox):', e);
  }

  pagBankInstance = PagSeguroDirectPayment;
  isInitialized = true;

  console.log('[PagBank SDK] Initialized:', { environment: env });
  return pagBankInstance;
}

/**
 * Generate a session ID for PagBank
 * In production, this should come from your backend via PagBank API
 */
function generateSessionId() {
  // For sandbox, we can use a simple random string
  // In production, call backend to create session via PagBank API
  return 'session_' + Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Create a card token using PagBank JS SDK
 * This opens the secure card fields/iframe and returns a token (CARD_XXXX)
 * 
 * @param {Object} cardData - Raw card data from user input
 * @param {string} cardData.number - Card number (no spaces)
 * @param {string} cardData.cvv - CVV code
 * @param {string} cardData.expiryMonth - MM format
 * @param {string} cardData.expiryYear - YYYY format
 * @param {string} cardData.holderName - Cardholder name
 * @param {string} cardData.holderCPF - Cardholder CPF (numbers only)
 * @returns {Promise<string>} Card token (CARD_XXXX)
 */
export async function createCardToken(cardData) {
  if (!isInitialized || !pagBankInstance) {
    await initPagBankSDK();
  }

  if (!pagBankInstance) {
    throw new Error('PagBank SDK not initialized. Call initPagBankSDK() first.');
  }

  return new Promise((resolve, reject) => {
    const { PagSeguroDirectPayment } = window;
    
    if (!PagSeguroDirectPayment) {
      reject(new Error('PagSeguroDirectPayment not available'));
      return;
    }

    // Create card token via PagBank SDK
    PagSeguroDirectPayment.createCardToken({
      cardNumber: cardData.number.replace(/\s/g, ''),
      cvv: cardData.cvv,
      expirationMonth: cardData.expiryMonth.toString().padStart(2, '0'),
      expirationYear: cardData.expiryYear.toString(),
      holderName: cardData.holderName,
      holderCPF: cardData.holderCPF.replace(/\D/g, ''),
      success: (response) => {
        if (response.card && response.card.token) {
          resolve(response.card.token); // CARD_XXXX
        } else {
          reject(new Error('Invalid response from PagBank: no token'));
        }
      },
      error: (response) => {
        const errors = response.errors || {};
        const messages = Object.values(errors).flat().join(', ');
        reject(new Error(messages || 'Failed to create card token'));
      },
      complete: () => {
        // Cleanup if needed
      },
    });
  });
}

/**
 * Get card brand from number (via PagBank SDK)
 * @param {string} cardNumber - Card number
 * @returns {Promise<string>} Brand (visa, mastercard, elo, amex, hipercard, etc.)
 */
export async function getCardBrand(cardNumber) {
  if (!isInitialized || !pagBankInstance) {
    await initPagBankSDK();
  }

  if (!pagBankInstance) {
    return 'unknown';
  }

  return new Promise((resolve) => {
    const { PagSeguroDirectPayment } = window;
    if (!PagSeguroDirectPayment) {
      resolve('unknown');
      return;
    }

    PagSeguroDirectPayment.getBrand({
      cardBin: cardNumber.replace(/\s/g, '').substring(0, 6),
      success: (response) => {
        resolve(response.brand?.name?.toLowerCase() || 'unknown');
      },
      error: () => {
        resolve('unknown');
      },
    });
  });
}

/**
 * Check if PagBank SDK is ready
 */
export function isPagBankReady() {
  return isInitialized && !!pagBankInstance;
}

/**
 * Get PagBank configuration from environment or localStorage
 */
export function getPagBankConfig() {
  // First check environment variable (set at build time)
  const envPublicKey = process.env.REACT_APP_PAGBANK_PUBLIC_KEY;
  const envEnvironment = process.env.REACT_APP_ENVIRONMENT === 'production' ? 'production' : 'sandbox';
  
  // Then check localStorage (set at runtime from backend)
  const storedSettings = typeof window !== 'undefined' 
    ? JSON.parse(localStorage.getItem('app_settings') || '{}') 
    : {};
  const storedPublicKey = storedSettings.pagbank_public_key;
  
  return {
    publicKey: envPublicKey || storedPublicKey || '',
    environment: envEnvironment,
  };
}

/**
 * Get PagBank instance for advanced usage
 */
export function getPagBankInstance() {
  return pagBankInstance;
}

// Export for React Native WebView or other integrations
export default {
  init: initPagBankSDK,
  createCardToken,
  getCardBrand,
  isReady: isPagBankReady,
  getInstance: getPagBankInstance,
};